import React, { useState, useEffect } from "react";
import api from "../services/api";

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarModalConfirmacion, setMostrarModalConfirmacion] = useState(false); // 👈 Nuevo modal
  const [editando, setEditando] = useState(false);
  const [usuarioId, setUsuarioId] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [cargandoDependencias, setCargandoDependencias] = useState(false);
  const [dependencias, setDependencias] = useState(null);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    password: "",
    rol: "visualizador",
    activo: true,
    departamentoId: "",
  });
  const [mensaje, setMensaje] = useState({ texto: "", tipo: "" });
  const [departamentos, setDepartamentos] = useState([]);
  const [cargandoDepartamentos, setCargandoDepartamentos] = useState(true);
  const [sinDepartamentos, setSinDepartamentos] = useState(false);
  const [creandoBasicos, setCreandoBasicos] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [planNombre, setPlanNombre] = useState(null);
  const [moduloContabilidad, setModuloContabilidad] = useState(false);

  useEffect(() => {
    // Detectar si es superadmin desde el token
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.is_superadmin) {
          setIsSuperAdmin(true);
          cargarTenants();
        }
      } catch(e) {}
    }
    const cargarTodo = async () => {
      await obtenerUsuarios();
      await cargarDepartamentos();
      await cargarPlan();
    };
    cargarTodo();
  }, []);

  // Cargar usuarios
  const obtenerUsuarios = async () => {
    try {
      setCargando(true);
      const res = await api.get("/usuarios");
      setUsuarios(res.data);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
      setMensaje({ texto: "❌ Error al cargar usuarios", tipo: "error" });
    } finally {
      setCargando(false);
    }
  };

  const cargarPlan = async () => {
    try {
      const res = await api.get("/configuracion");
      setPlanNombre(res.data?.plan_nombre || null);
      setModuloContabilidad(res.data?.modulo_contabilidad === true);
    } catch (error) {
      console.error("Error al cargar plan:", error);
    }
  };

  // Cargar tenants (solo superadmin)
  const cargarTenants = async () => {
    try {
      const res = await api.get("/superadmin/tenants");
      setTenants(res.data.tenants || []);
    } catch (error) {
      console.error("Error al cargar tenants:", error);
    }
  };

  // Cargar departamentos (opcionalmente de un tenant específico para superadmin)
  const cargarDepartamentos = async (tenantIdOverride = null) => {
    try {
      const url = tenantIdOverride
        ? `/superadmin/tenants/${tenantIdOverride}/departamentos`
        : "/departamentos";
      const res = await api.get(url);
      setDepartamentos(res.data);
      setSinDepartamentos(res.data.length === 0);
    } catch (error) {
      console.error("Error al cargar departamentos:", error);
    } finally {
      setCargandoDepartamentos(false);
    }
  };

  // ============================================
  // NUEVA FUNCIÓN: Verificar dependencias antes de eliminar
  // ============================================
  const verificarAntesDeEliminar = async (usuario) => {
    try {
      setCargandoDependencias(true);
      setUsuarioSeleccionado(usuario);
      
      // Llamar al nuevo endpoint
      const res = await api.get(`/usuarios/${usuario.id}/dependencias`);
      setDependencias(res.data.dependencias);
      
      // Mostrar el modal de confirmación
      setMostrarModalConfirmacion(true);
    } catch (error) {
      console.error("Error al verificar dependencias:", error);
      setMensaje({ texto: "❌ Error al verificar dependencias del usuario", tipo: "error" });
    } finally {
      setCargandoDependencias(false);
    }
  };

  // ============================================
  // NUEVA FUNCIÓN: Ejecutar la eliminación (si procede)
  // ============================================
  const confirmarEliminar = async () => {
    if (!usuarioSeleccionado) return;

    try {
      const res = await api.delete(`/usuarios/${usuarioSeleccionado.id}`);
      
      // Cerrar modal
      setMostrarModalConfirmacion(false);
      setUsuarioSeleccionado(null);
      setDependencias(null);
      
      // Mostrar mensaje según el tipo de eliminación
      if (res.data.tipo === 'soft_delete') {
        setMensaje({ 
          texto: `✅ Usuario inactivado. Tenía ${res.data.dependencias.total} registros asociados.`, 
          tipo: "success" 
        });
      } else {
        setMensaje({ texto: "✅ Usuario eliminado permanentemente", tipo: "success" });
      }
      
      // Recargar lista
      obtenerUsuarios();
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 4000);
      
    } catch (error) {
      console.error("Error al eliminar usuario:", error);
      const errorMsg = error.response?.data?.error || "Error al eliminar usuario";
      setMensaje({ texto: `❌ ${errorMsg}`, tipo: "error" });
    }
  };

  // ============================================
  // NUEVA FUNCIÓN: Reactivar usuario
  // ============================================
  const reactivarUsuario = async (id) => {
    if (!window.confirm("¿Estás seguro de reactivar este usuario?")) return;

    try {
      await api.patch(`/usuarios/${id}/reactivar`);
      setMensaje({ texto: "✅ Usuario reactivado correctamente", tipo: "success" });
      obtenerUsuarios();
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 3000);
    } catch (error) {
      console.error("Error al reactivar usuario:", error);
      setMensaje({ texto: "❌ Error al reactivar usuario", tipo: "error" });
    }
  };

  // Abrir modal de crear/editar
  const abrirModal = (usuario = null) => {
    if (usuario) {
      setEditando(true);
      setUsuarioId(usuario.id);
      setForm({
        nombre: usuario.nombre,
        email: usuario.email,
        password: "",
        rol: usuario.rol,
        activo: usuario.activo,
        departamentoId: usuario.departamentoId || "",
      });
      // Si es superadmin y el usuario es de otro tenant, cargar departamentos de ese tenant
      if (isSuperAdmin && usuario.tenant_id) {
        cargarDepartamentos(usuario.tenant_id);
      }
    } else {
      setEditando(false);
      setUsuarioId(null);
      const esContable = moduloContabilidad || isSuperAdmin;
      setForm({
        nombre: "",
        email: "",
        password: "",
        rol: esContable ? "auxiliar_contable" : "visualizador",
        activo: true,
        departamentoId: "",
      });
      // Si es superadmin creando usuario nuevo, limpiar departamentos (sin tenant aún)
      if (isSuperAdmin) setDepartamentos([]);
    }
    setMostrarModal(true);
    setMensaje({ texto: "", tipo: "" });
  };

  const cerrarModal = () => {
    setMostrarModal(false);
    setForm({
      nombre: "",
      email: "",
      password: "",
      rol: "visualizador",
      activo: true,
    });
    setMensaje({ texto: "", tipo: "" });
  };

  const cerrarModalConfirmacion = () => {
    setMostrarModalConfirmacion(false);
    setUsuarioSeleccionado(null);
    setDependencias(null);
  };

  const guardarUsuario = async () => {
    // Validaciones
    if (!form.nombre || !form.email) {
      setMensaje({ texto: "⚠️ Nombre y email son obligatorios", tipo: "error" });
      return;
    }

    if (!editando && !form.password) {
      setMensaje({ texto: "⚠️ La contraseña es obligatoria para nuevos usuarios", tipo: "error" });
      return;
    }

    try {
      if (editando) {
        const datos = { 
          nombre: form.nombre, 
          email: form.email, 
          rol: form.rol, 
          activo: form.activo, 
          departamentoId: form.departamentoId || null 
        };
        if (form.password) {
          datos.password = form.password;
        }
        await api.put(`/usuarios/${usuarioId}`, datos);
        setMensaje({ texto: "✅ Usuario actualizado correctamente", tipo: "success" });
      } else {
        await api.post("/usuarios", form);
        setMensaje({ texto: "✅ Usuario creado correctamente", tipo: "success" });
      }

      setTimeout(() => {
        cerrarModal();
        obtenerUsuarios();
      }, 1500);

    } catch (error) {
      console.error("Error al guardar usuario:", error);
      const errorMsg = error.response?.data?.error || "Error al guardar usuario";
      setMensaje({ texto: `❌ ${errorMsg}`, tipo: "error" });
    }
  };

  const getRoleBadge = (rol) => {
    const roles = {
      superadmin:          { color: '#667eea', icon: '⚡', label: 'Super Admin' },
      admin:               { color: '#8b5cf6', icon: '👑', label: 'Admin' },
      supervisor:          { color: '#0ea5e9', icon: '🧑‍💼', label: 'Supervisor' },
      supervisor_pos:      { color: '#f59e0b', icon: '🛒', label: 'Supervisor POS' },
      almacenero:          { color: '#3b82f6', icon: '📦', label: 'Almacenero' },
      cajero:              { color: '#10b981', icon: '💰', label: 'Cajero' },
      visualizador:        { color: '#64748b', icon: '👁️', label: 'Visualizador' },
      contador:            { color: '#0d9488', icon: '📒', label: 'Contador' },
      auxiliar_contable:   { color: '#7c3aed', icon: '🖊️', label: 'Auxiliar Contable' },
      supervisor_contable: { color: '#1d4ed8', icon: '🔍', label: 'Supervisor Contable' },
    };
    const roleInfo = roles[rol] || roles.visualizador;
    return (
      <span style={{...styles.badge, background: roleInfo.color}}>
        {roleInfo.icon} {roleInfo.label}
      </span>
    );
  };

  // Crear departamentos básicos
  const crearDepartamentosBasicos = async () => {
    setCreandoBasicos(true);
    try {
      const res = await api.post("/departamentos/basicos");
      setMensaje({ texto: res.data.mensaje, tipo: "success" });
      await cargarDepartamentos();
      setTimeout(() => setMensaje({ texto: "", tipo: "" }), 4000);
    } catch (error) {
      setMensaje({ texto: "❌ Error al crear departamentos básicos", tipo: "error" });
    } finally {
      setCreandoBasicos(false);
    }
  };

  // Colores por tenant — secuencia de 10 colores que se repite
  const TENANT_COLORS = [
    { bg: '#ede9fe', color: '#6d28d9', border: '#8b5cf6' }, // morado
    { bg: '#dbeafe', color: '#1d4ed8', border: '#3b82f6' }, // azul
    { bg: '#d1fae5', color: '#065f46', border: '#10b981' }, // verde
    { bg: '#fef3c7', color: '#92400e', border: '#f59e0b' }, // amarillo
    { bg: '#fee2e2', color: '#991b1b', border: '#ef4444' }, // rojo
    { bg: '#e0f2fe', color: '#0369a1', border: '#0ea5e9' }, // celeste
    { bg: '#fce7f3', color: '#9d174d', border: '#ec4899' }, // rosado
    { bg: '#f0fdf4', color: '#166534', border: '#22c55e' }, // verde claro
    { bg: '#fff7ed', color: '#9a3412', border: '#f97316' }, // naranja
    { bg: '#f1f5f9', color: '#334155', border: '#64748b' }, // gris
  ];

  const getTenantColor = (tenantId) => {
    if (!tenantId) return TENANT_COLORS[9];
    return TENANT_COLORS[(tenantId - 1) % 10];
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>👥 Gestión de Usuarios</h1>
          <p style={styles.subtitle}>Administra los usuarios del sistema</p>
        </div>
        <button style={styles.btnAdd} onClick={() => abrirModal()}>
          ➕ Nuevo Usuario
        </button>
      </div>

      {/* Mensaje global */}
      {mensaje.texto && !mostrarModal && !mostrarModalConfirmacion && (
        <div style={{
          ...styles.message,
          ...(mensaje.tipo === "success" ? styles.messageSuccess : styles.messageError)
        }}>
          {mensaje.texto}
        </div>
      )}

      {/* Estadísticas */}
      {(() => {
        const esContable = moduloContabilidad || isSuperAdmin;
        return (
          <div style={styles.statsGrid}>
            {isSuperAdmin && (
              <div style={styles.statCard}>
                <div style={{...styles.statIcon, background: '#667eea'}}>⚡</div>
                <div style={styles.statContent}>
                  <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'superadmin').length}</div>
                  <div style={styles.statLabel}>Super Admins</div>
                </div>
              </div>
            )}
            <div style={styles.statCard}>
              <div style={{...styles.statIcon, background: '#8b5cf6'}}>👑</div>
              <div style={styles.statContent}>
                <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'admin').length}</div>
                <div style={styles.statLabel}>Administradores</div>
              </div>
            </div>
            {esContable ? (
              <>
                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: '#0d9488'}}>📒</div>
                  <div style={styles.statContent}>
                    <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'contador').length}</div>
                    <div style={styles.statLabel}>Contadores</div>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: '#7c3aed'}}>🖊️</div>
                  <div style={styles.statContent}>
                    <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'auxiliar_contable').length}</div>
                    <div style={styles.statLabel}>Auxiliares Contables</div>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: '#1d4ed8'}}>🔍</div>
                  <div style={styles.statContent}>
                    <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'supervisor_contable').length}</div>
                    <div style={styles.statLabel}>Supervisores Contables</div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: '#0ea5e9'}}>🧑‍💼</div>
                  <div style={styles.statContent}>
                    <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'supervisor').length}</div>
                    <div style={styles.statLabel}>Supervisores</div>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: '#f59e0b'}}>🛒</div>
                  <div style={styles.statContent}>
                    <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'supervisor_pos').length}</div>
                    <div style={styles.statLabel}>Supervisores POS</div>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: '#3b82f6'}}>📦</div>
                  <div style={styles.statContent}>
                    <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'almacenero').length}</div>
                    <div style={styles.statLabel}>Almaceneros</div>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: '#10b981'}}>💰</div>
                  <div style={styles.statContent}>
                    <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'cajero').length}</div>
                    <div style={styles.statLabel}>Cajeros</div>
                  </div>
                </div>
                <div style={styles.statCard}>
                  <div style={{...styles.statIcon, background: '#64748b'}}>👁️</div>
                  <div style={styles.statContent}>
                    <div style={styles.statNumber}>{usuarios.filter(u => u.rol === 'visualizador').length}</div>
                    <div style={styles.statLabel}>Visualizadores</div>
                  </div>
                </div>
              </>
            )}
            <div style={styles.statCard}>
              <div style={{...styles.statIcon, background: '#10b981'}}>✓</div>
              <div style={styles.statContent}>
                <div style={styles.statNumber}>{usuarios.filter(u => u.activo).length}</div>
                <div style={styles.statLabel}>Activos</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={{...styles.statIcon, background: '#ef4444'}}>✕</div>
              <div style={styles.statContent}>
                <div style={styles.statNumber}>{usuarios.filter(u => !u.activo).length}</div>
                <div style={styles.statLabel}>Inactivos</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ⚠️ WARNING: Sin departamentos */}
      {sinDepartamentos && (
        <div style={{
          background: '#fffbeb',
          border: '1px solid #f59e0b',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div>
              <p style={{ margin: 0, fontWeight: '700', color: '#92400e', fontSize: '14px' }}>
                No tienes departamentos creados
              </p>
              <p style={{ margin: '2px 0 0', color: '#b45309', fontSize: '13px' }}>
                Los usuarios necesitan un departamento. Crea los básicos de un click o ve a Departamentos para personalizarlos.
              </p>
            </div>
          </div>
          <button
            onClick={crearDepartamentosBasicos}
            disabled={creandoBasicos}
            style={{
              padding: '10px 18px',
              background: creandoBasicos ? '#d97706' : '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '700',
              cursor: creandoBasicos ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {creandoBasicos ? '⏳ Creando...' : '🏢 Crear Departamentos Básicos'}
          </button>
        </div>
      )}

      {/* Tabla */}
      <div style={styles.tableCard}>
        <h3 style={styles.cardTitle}>📋 Lista de Usuarios</h3>

        {cargando ? (
          <div style={styles.loadingContainer}>
            <p style={styles.loadingText}>Cargando usuarios...</p>
          </div>
        ) : usuarios.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>👥</span>
            <p style={styles.emptyText}>No hay usuarios registrados</p>
          </div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Usuario</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Rol</th>
                  {isSuperAdmin && <th style={styles.th}>Empresa</th>}
                  <th style={styles.th}>Estado</th>
                  <th style={styles.th}>Departamento</th>
                  <th style={styles.th}>Último Acceso</th>
                  <th style={styles.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.map((usuario) => (
                  <tr 
                    key={usuario.id}
                    style={{...styles.tr, opacity: usuario.activo ? 1 : 0.7}}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    {isSuperAdmin && (
                      <td style={styles.td}>
                        {(() => {
                          const c = getTenantColor(usuario.tenant_id);
                          return (
                            <span style={{
                              background: c.bg,
                              color: c.color,
                              border: `1px solid ${c.border}`,
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '700',
                              whiteSpace: 'nowrap'
                            }}>
                              #{usuario.tenant_id} {usuario.tenant_nombre || '—'}
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    <td style={styles.td}>
                      <div style={styles.userInfo}>
                        <div style={styles.avatar}>
                          {usuario.nombre.charAt(0).toUpperCase()}
                        </div>
                        <strong>{usuario.nombre}</strong>
                      </div>
                    </td>
                    <td style={styles.td}>{usuario.email}</td>
                    <td style={styles.td}>{getRoleBadge(usuario.rol)}</td>
                    {isSuperAdmin && (
                      <td style={styles.td}>
                        <span style={{background:'#e0e7ff', color:'#3730a3', padding:'3px 10px', borderRadius:12, fontSize:12, fontWeight:600}}>
                          {usuario.tenant_nombre || (usuario.tenant_id ? `Empresa #${usuario.tenant_id}` : '🌐 Global')}
                        </span>
                      </td>
                    )}
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        ...(usuario.activo ? styles.statusActive : styles.statusInactive)
                      }}>
                        {usuario.activo ? '✓ Activo' : '✕ Inactivo'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {usuario.Departamento?.nombre || '—'}
                    </td>
                    <td style={styles.td}>
                      {usuario.ultimo_acceso 
                        ? new Date(usuario.ultimo_acceso).toLocaleDateString('es-DO', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Nunca'}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          style={styles.btnEdit}
                          onClick={() => abrirModal(usuario)}
                          title="Editar usuario"
                        >
                          ✏️
                        </button>
                        {usuario.activo ? (
                          <button
                            style={styles.btnDelete}
                            onClick={() => verificarAntesDeEliminar(usuario)}
                            title="Eliminar/Inactivar usuario"
                          >
                            🗑️
                          </button>
                        ) : (
                          <button
                            style={styles.btnReactivate}
                            onClick={() => reactivarUsuario(usuario.id)}
                            title="Reactivar usuario"
                          >
                            🔄
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Crear/Editar */}
      {mostrarModal && (
        <div style={styles.modalOverlay} onClick={cerrarModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {editando ? '✏️ Editar Usuario' : '➕ Nuevo Usuario'}
              </h3>
              <button style={styles.closeBtn} onClick={cerrarModal}>✕</button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Nombre Completo *</label>
                <input
                  type="text"
                  style={styles.input}
                  placeholder="Ej: Juan Pérez"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Email *</label>
                <input
                  type="email"
                  style={styles.input}
                  placeholder="usuario@ejemplo.com"
                  autoComplete="off"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Contraseña {editando ? '(dejar vacío para no cambiar)' : '*'}
                </label>
                <input
                  type="password"
                  style={styles.input}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>

              {(() => {
                const esContable = moduloContabilidad || isSuperAdmin;
                return (
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Rol *</label>
                  <select
                    style={styles.select}
                    value={form.rol}
                    onChange={(e) => setForm({ ...form, rol: e.target.value })}
                  >
                    <optgroup label="— Roles Generales —">
                      <option value="visualizador">👁️ Visualizador</option>
                      <option value="cajero">💰 Cajero</option>
                      <option value="almacenero">📦 Almacenero</option>
                      <option value="supervisor">🧑‍💼 Supervisor</option>
                      <option value="supervisor_pos">🛒 Supervisor POS</option>
                      <option value="admin">👑 Administrador</option>
                    </optgroup>
                    {esContable && (
                      <optgroup label="— Roles Contables —">
                        <option value="auxiliar_contable">🖊️ Auxiliar Contable</option>
                        <option value="contador">📒 Contador</option>
                        <option value="supervisor_contable">🔍 Supervisor Contable</option>
                      </optgroup>
                    )}
                    {isSuperAdmin && <option value="superadmin">⚡ Super Admin</option>}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Departamento</label>
                  <select
                    style={styles.select}
                    value={form.departamentoId}
                    onChange={(e) => setForm({ ...form, departamentoId: e.target.value || null })}
                  >
                    <option value="">-- Seleccionar --</option>
                    {departamentos.map(depto => (
                      <option key={depto.id} value={depto.id}>
                        {depto.nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Estado</label>
                  <select
                    style={styles.select}
                    value={form.activo}
                    onChange={(e) => setForm({ ...form, activo: e.target.value === 'true' })}
                  >
                    <option value="true">✓ Activo</option>
                    <option value="false">✕ Inactivo</option>
                  </select>
                </div>
              </div>
              );
              })()}

              {/* Info de roles */}
              <div style={styles.infoBox}>
                <p style={styles.helpText}>
                  {form.rol === 'visualizador' && '📝 Solo puede ver y crear requisiciones'}
                  {form.rol === 'cajero' && '🛒 Acceso a POS y gestión de cajas'}
                  {form.rol === 'almacenero' && '📦 Gestión completa de inventario'}
                  {form.rol === 'supervisor' && '📊 Supervisión, aprobaciones y reportes'}
                  {form.rol === 'supervisor_pos' && '🛒 Gestión de precios y supervisión POS'}
                  {form.rol === 'admin' && '👤 Gestión de usuarios y configuraciones'}
                  {form.rol === 'superadmin' && '⚙️ Control total incluyendo activación de módulos'}
                  {form.rol === 'contador' && '📒 Acceso a contabilidad, facturación, DGII y nómina'}
                  {form.rol === 'auxiliar_contable' && '🖊️ Registro de transacciones y soporte contable'}
                  {form.rol === 'supervisor_contable' && '🔍 Supervisión contable, aprobaciones y reportes'}
                </p>
              </div>

              {mensaje.texto && (
                <div style={{
                  ...styles.message,
                  ...(mensaje.tipo === "success" ? styles.messageSuccess : styles.messageError)
                }}>
                  {mensaje.texto}
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.btnCancel} onClick={cerrarModal}>
                Cancelar
              </button>
              <button style={styles.btnSave} onClick={guardarUsuario}>
                {editando ? '💾 Actualizar' : '✓ Crear Usuario'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* NUEVO MODAL: Confirmación de eliminación con dependencias */}
      {/* ============================================ */}
      {mostrarModalConfirmacion && (
        <div style={styles.modalOverlay} onClick={cerrarModalConfirmacion}>
          <div style={{...styles.modal, maxWidth: '500px'}} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>⚠️ Confirmar eliminación</h3>
              <button style={styles.closeBtn} onClick={cerrarModalConfirmacion}>✕</button>
            </div>

            <div style={styles.modalBody}>
              {cargandoDependencias ? (
                <div style={{textAlign: 'center', padding: '30px'}}>
                  <p>Verificando dependencias...</p>
                </div>
              ) : (
                <>
                  <p style={{fontSize: '16px', marginBottom: '15px'}}>
                    Usuario: <strong>{usuarioSeleccionado?.nombre}</strong>
                  </p>

                  {dependencias && dependencias.total > 0 ? (
                    <>
                      <div style={{
                        background: '#fef3c7',
                        border: '1px solid #f59e0b',
                        borderRadius: '8px',
                        padding: '15px',
                        marginBottom: '20px'
                      }}>
                        <p style={{fontWeight: 'bold', margin: '0 0 10px 0', color: '#92400e'}}>
                          ⚠️ Este usuario tiene {dependencias.total} registro(s) asociado(s):
                        </p>
                        <ul style={{margin: '0', paddingLeft: '20px', color: '#92400e'}}>
                          {dependencias.crm_oportunidades > 0 && 
                            <li>📊 Oportunidades CRM: {dependencias.crm_oportunidades}</li>}
                          {dependencias.crm_prospectos_asignado > 0 && 
                            <li>📋 Prospectos asignados: {dependencias.crm_prospectos_asignado}</li>}
                          {dependencias.crm_prospectos_creado > 0 && 
                            <li>➕ Prospectos creados: {dependencias.crm_prospectos_creado}</li>}
                          {dependencias.crm_seguimientos > 0 && 
                            <li>📝 Seguimientos: {dependencias.crm_seguimientos}</li>}
                          {dependencias.cotizaciones > 0 && 
                            <li>📄 Cotizaciones: {dependencias.cotizaciones}</li>}
                          {dependencias.facturas > 0 && 
                            <li>🧾 Facturas: {dependencias.facturas}</li>}
                          {dependencias.pagos_recibidos > 0 && 
                            <li>💰 Pagos recibidos: {dependencias.pagos_recibidos}</li>}
                          {dependencias.logs_auditoria > 0 && 
                            <li>📋 Logs de auditoría: {dependencias.logs_auditoria}</li>}
                          {dependencias.requisiciones_procesadas > 0 && 
                            <li>✅ Requisiciones procesadas: {dependencias.requisiciones_procesadas}</li>}
                          {dependencias.requisiciones_creadas > 0 && 
                            <li>📋 Requisiciones creadas: {dependencias.requisiciones_creadas}</li>}
                        </ul>
                      </div>
                      <p style={{color: '#475569', fontSize: '14px'}}>
                        <strong>Acción recomendada:</strong> El usuario será <strong>INACTIVADO</strong> (soft delete). 
                        Los registros históricos se conservarán pero el usuario no podrá acceder al sistema.
                      </p>
                    </>
                  ) : (
                    <div style={{
                      background: '#d1fae5',
                      border: '1px solid #10b981',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '20px'
                    }}>
                      <p style={{color: '#065f46', fontWeight: 'bold', margin: '0'}}>
                        ✅ Este usuario no tiene registros asociados. Puede ser eliminado permanentemente.
                      </p>
                    </div>
                  )}
                </>
              )}

              {mensaje.texto && (
                <div style={{
                  ...styles.message,
                  ...(mensaje.tipo === "success" ? styles.messageSuccess : styles.messageError)
                }}>
                  {mensaje.texto}
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.btnCancel} onClick={cerrarModalConfirmacion}>
                Cancelar
              </button>
              <button 
                style={{
                  ...styles.btnDelete,
                  padding: '10px 20px',
                  fontSize: '14px'
                }}
                onClick={confirmarEliminar}
                disabled={cargandoDependencias}
              >
                {dependencias?.total > 0 ? '🔽 Inactivar Usuario' : '🗑️ Eliminar Permanentemente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f8fafc',
    padding: '20px',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0 0 5px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
  },
  btnAdd: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
  },
  message: {
    padding: '12px 20px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'center',
  },
  messageSuccess: {
    background: '#d1fae5',
    color: '#065f46',
    border: '1px solid #10b981',
  },
  messageError: {
    background: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #ef4444',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '15px',
    marginBottom: '25px',
  },
  statCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    color: 'white',
  },
  statContent: {
    flex: '1',
  },
  statNumber: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: '2px',
  },
  statLabel: {
    fontSize: '11px',
    color: '#64748b',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  tableCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '25px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    border: '1px solid #e2e8f0',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '20px',
  },
  loadingContainer: {
    padding: '40px',
    textAlign: 'center',
  },
  loadingText: {
    color: '#64748b',
    fontSize: '14px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '15px',
    display: 'block',
    opacity: '0.5',
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#475569',
    margin: '0',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    background: '#f8fafc',
    padding: '12px',
    textAlign: 'left',
    fontWeight: '700',
    color: '#475569',
    borderBottom: '2px solid #e2e8f0',
  },
  tr: {
    transition: 'background 0.2s ease',
  },
  td: {
    padding: '12px',
    borderBottom: '1px solid #f1f5f9',
    color: '#1e293b',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '14px',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    color: 'white',
    display: 'inline-block',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-block',
  },
  statusActive: {
    background: '#d1fae5',
    color: '#065f46',
  },
  statusInactive: {
    background: '#fee2e2',
    color: '#991b1b',
  },
  actions: {
    display: 'flex',
    gap: '8px',
  },
  btnEdit: {
    padding: '6px 12px',
    background: '#fbbf24',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  btnDelete: {
    padding: '6px 12px',
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  btnReactivate: {
    padding: '6px 12px',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 25px',
    borderBottom: '1px solid #e2e8f0',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1e293b',
    margin: '0',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#94a3b8',
  },
  modalBody: {
    padding: '25px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    marginBottom: '8px',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    background: 'white',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  infoBox: {
    background: '#eff6ff',
    border: '1px solid #3b82f6',
    borderRadius: '8px',
    padding: '15px',
    marginTop: '20px',
  },
  helpText: {
    fontSize: '12px',
    color: '#1e40af',
    margin: '0',
    fontStyle: 'italic',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '20px 25px',
    borderTop: '1px solid #e2e8f0',
  },
  btnCancel: {
    padding: '10px 20px',
    background: '#94a3b8',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  btnSave: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};

export default Usuarios;